import React, {useMemo, useState} from "react";
import "./index.scss";
import {Autocomplete} from "@material-ui/lab"
import {CircularProgress, TextField, FormControl} from "@material-ui/core";
import KeyboardArrowDownOutlined from '@material-ui/icons/KeyboardArrowDownOutlined'
import { strings } from "../../../strings/strings";
import {AutocompleteProps} from "@material-ui/lab/Autocomplete/Autocomplete";

export interface AutocompleteValue {
    value: string;
    label: string;
}

export interface ShortcutData extends AutocompleteValue {
    labelShort: string;
}

interface Props extends Partial<Omit<AutocompleteProps<AutocompleteValue, boolean, boolean, boolean>, 'onChange'>> {
    name?: string;
    value?: AutocompleteValue;
    placeholder?: string;
    loadFunction?: (query?: string) => Promise<any[]>;
    charactersToSearch?: number;
    noOptionsText?: string;
    shortcutData?: ShortcutData[];
    disabled?: boolean;
    hidden?: boolean;
    error?: boolean;
    validationError?: string;
    onChange?: (value: any) => void;
    formatResponse?: (obj: AutocompleteValue | string) => any;
    formatValue?: (obj: any) => AutocompleteValue;
    formatOptions?: (obj: any ) => AutocompleteValue;
}

export const SelectAutocomplete = (props: Props) => {
    const {
        name,
        value,
        disabled,
        hidden,
        error,
        validationError,
        placeholder,
        loadFunction,
        charactersToSearch,
        noOptionsText,
        shortcutData,
        onChange,
        formatResponse,
        formatValue,
        formatOptions,
        ...rest
    } = props;

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<AutocompleteValue[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const setValue = (value: string | AutocompleteValue) => {
        const normalizedValue = value || {value: null, label: null};
        const formattedValue = formatResponse ? formatResponse(normalizedValue) : normalizedValue;

        onChange(formattedValue);
    };

    const loadData = async (value?: string) => {
        try {
            setLoading(true);

            const response = await loadFunction(value);
            const formattedOptions = formatOptions ? response.map(formatOptions) : response;

            setOptions(formattedOptions);
            setLoading(false);
        } catch (e) {
            setLoading(false);
        }
    }

    const handleChange = (_, value) => {
        setValue(value);
    };

    const handleChangeText = (event: React.ChangeEvent<{ value: string }>) => {
        let inputValue = event.target.value ? event.target.value.toString() : '';

        if (typeof inputValue === 'string' && inputValue.length >= charactersToSearch) {
            return loadData(inputValue)
        }
    };

    const handleOpen = () => {
        setOpen(true);
        if (options.length === 0 && !charactersToSearch) {
            return loadData()
        }
    }

    const handleClose = () => {
        setOpen(false);
        setOptions([]);
    }

    const fieldValue = useMemo(() => formatValue ? formatValue(value) : value, [value]);

    return (
        <div className={'select-autocomplete'}>
            <FormControl className={`select-one-input__select ${open ? 'arrow-style-open' : 'arrow-style-close'}`} error={error} variant="outlined" fullWidth={true}>
                <Autocomplete
                    {...rest}
                    hidden={hidden}
                    id={name}
                    options={options}
                    open={open}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={handleChange}
                    getOptionLabel={(option) => option.label || ''}
                    loading={loading}
                    disabled={disabled}
                    value={fieldValue}
                    popupIcon={<KeyboardArrowDownOutlined />}
                    noOptionsText={noOptionsText || ""}
                    title={fieldValue?.label}
                    clearText={strings.actions.clear}
                    closeText={strings.actions.close}
                    openText={strings.actions.open}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={error}
                            onChange={handleChangeText}
                            helperText={error && validationError}
                            label={placeholder}
                            variant="outlined"
                            SelectProps={{
                                MenuProps: {
                                    transitionDuration: 0,
                                }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}/>
                    )}
                />
                {shortcutData !== undefined && (
                    <div className={'shortcut-data'}>
                        {shortcutData.map((item) => {
                            return <span key={item.value} onClick={() => {
                                setValue(item)
                            }}>{item.labelShort || item.label}</span>
                        })}

                    </div>
                )}
            </FormControl>
        </div>
    )
};
